import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock } from 'lucide-react';

import { useSalaAuth } from '../auth/SalaAuthContext';
import { PostAuthLoadingScreen } from '../components/auth/PostAuthLoadingScreen';

const POST_AUTH_HOLD_MS = 1000;

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo') || '/studio';
  const { user, profileComplete, loading, firebaseReady, signInEmail, registerEmail, signInGoogle } =
    useSalaAuth();

  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [entering, setEntering] = useState(false);

  useEffect(() => {
    if (loading || !user) return;
    setEntering(true);
    const dest = profileComplete
      ? returnTo
      : `/onboarding?returnTo=${encodeURIComponent(returnTo)}`;
    const timer = window.setTimeout(() => {
      navigate(dest, { replace: true });
    }, POST_AUTH_HOLD_MS);
    return () => window.clearTimeout(timer);
  }, [loading, user, profileComplete, navigate, returnTo]);

  if (entering || (!loading && user)) {
    return (
      <PostAuthLoadingScreen
        label={profileComplete ? 'Abriendo Studio…' : 'Preparando tu perfil…'}
      />
    );
  }

  const mapError = (err: unknown): string => {
    const code =
      err && typeof err === 'object' && 'code' in err ? String((err as { code: string }).code) : '';
    const message =
      err instanceof Error ? err.message : typeof err === 'string' ? err : '';

    if (code.includes('email-already-in-use')) return 'Ese correo ya tiene una cuenta. Inicia sesión.';
    if (
      code.includes('invalid-credential') ||
      code.includes('wrong-password') ||
      code.includes('user-not-found') ||
      code.includes('invalid-email')
    ) {
      return 'Correo o contraseña incorrectos.';
    }
    if (code.includes('weak-password')) return 'La contraseña debe tener al menos 6 caracteres.';
    if (code.includes('popup-closed') || code.includes('cancelled-popup')) {
      return 'Cerraste la ventana de Google. Intenta de nuevo.';
    }
    if (code.includes('popup-blocked')) {
      return 'El navegador bloqueó la ventana de Google. Reintentando con redirección…';
    }
    if (code.includes('unauthorized-domain')) {
      return 'Este dominio no está autorizado en Firebase Auth. Agrega sala.edusico.cl en Authorized domains.';
    }
    if (code.includes('operation-not-allowed')) {
      return 'Este método de acceso no está habilitado en Firebase (Email/Password o Google).';
    }
    if (code.includes('network-request-failed')) {
      return 'Sin conexión con Firebase. Revisa red o bloqueadores.';
    }
    if (code.includes('too-many-requests')) {
      return 'Demasiados intentos. Espera un momento e intenta otra vez.';
    }
    if (message.includes('Faltan variables') || message.includes('Firebase')) {
      return message;
    }
    if (code) return `No se pudo completar el acceso (${code}).`;
    return 'No se pudo completar el acceso. Revisa los datos e intenta otra vez.';
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mode === 'login') {
        await signInEmail(email, password);
      } else {
        if (name.trim().length < 2) {
          setError('Escribe tu nombre completo.');
          return;
        }
        await registerEmail(name, email, password);
      }
    } catch (err) {
      setError(mapError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleGoogle = async () => {
    setError(null);
    setBusy(true);
    try {
      await signInGoogle();
    } catch (err) {
      setError(mapError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sala-login">
      <div className="sala-login__panel">
        <Link to="/" className="sala-login__back">
          ← Volver
        </Link>

        <p className="sala-login__eyebrow">Acceso docente</p>
        <h1 className="sala-login__title">{mode === 'login' ? 'Entra a Studio' : 'Crea tu cuenta'}</h1>
        <p className="sala-login__lead">
          Tu espacio para preparar clases, material y rúbricas.
        </p>

        {!firebaseReady ? (
          <p className="sala-login__warn" role="status">
            Firebase no está configurado en este entorno.
          </p>
        ) : (
          <form className="sala-login__form" onSubmit={(event) => void handleSubmit(event)}>
            {mode === 'register' ? (
              <label className="sala-login__label">
                Nombre completo
                <input
                  className="sala-input-dark mt-1.5 w-full"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="Ej: Ana Pérez"
                  autoComplete="name"
                  required
                />
              </label>
            ) : null}

            <label className="sala-login__label">
              Correo
              <div className="relative mt-1.5">
                <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--micelio-muted)]" />
                <input
                  className="sala-input-dark w-full pl-10"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="tu@correo.cl"
                  autoComplete="email"
                  required
                />
              </div>
            </label>

            <label className="sala-login__label">
              Contraseña
              <div className="relative mt-1.5">
                <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--micelio-muted)]" />
                <input
                  className="sala-input-dark w-full pl-10 pr-10"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--micelio-muted)]"
                  onClick={() => setShowPassword((value) => !value)}
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </label>

            {error ? (
              <p className="sala-login__error" role="alert">
                {error}
              </p>
            ) : null}

            <button type="submit" className="sala-landing-cta w-full" disabled={busy}>
              {busy ? 'Espera…' : mode === 'login' ? 'Entrar' : 'Crear cuenta'}
            </button>

            <button
              type="button"
              className="sala-login__google"
              onClick={() => void handleGoogle()}
              disabled={busy}
            >
              Continuar con Google
            </button>

            <p className="sala-login__switch">
              {mode === 'login' ? (
                <>
                  ¿No tienes cuenta?{' '}
                  <button type="button" className="sala-login__switch-btn" onClick={() => setMode('register')}>
                    Regístrate
                  </button>
                </>
              ) : (
                <>
                  ¿Ya tienes cuenta?{' '}
                  <button type="button" className="sala-login__switch-btn" onClick={() => setMode('login')}>
                    Inicia sesión
                  </button>
                </>
              )}
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
